import { Component, OnDestroy } from "@angular/core";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";

@Component({
  selector: "receipt-util-tests-upload",
  templateUrl: "./receipt-util-tests-upload.component.html",
  styleUrls: ["./receipt-util-tests-upload.component.scss"],
})
export class ReceiptUtilTestsUploadComponent implements OnDestroy {
  public originalSize: number = 0;
  public compressedSize: number = 0;
  public compressedDataRaw: string | null = null;
  public originalPreviewUrl: SafeUrl | null = null;
  public compressedDataUrl: SafeUrl | null = null;
  public inputCompressionLevel: number = 50;
  public selectedCompressionLevel: number = this.inputCompressionLevel;
  private originalPreviewRaw: string | null = null;
  private originalFile: File | null = null;

  constructor(private sanitizer: DomSanitizer) {}

  public async compressImage(blobImg: Blob, percent: number) {
    const bitmap = await createImageBitmap(blobImg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Canvas 2D context not available");
    }
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    ctx.drawImage(bitmap, 0, 0);
    return canvas.toDataURL("image/jpeg", percent / 100);
  }

  public bytesToMegabytes(bytes: number): string {
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  }

  public async onInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) {
      return;
    }

    // store original file for potential re-compression
    this.originalFile = file;

    if (this.originalPreviewRaw) {
      try {
        URL.revokeObjectURL(this.originalPreviewRaw);
      } catch (e) {}
      this.originalPreviewRaw = null;
      this.originalPreviewUrl = null;
    }

    const blobUrl = URL.createObjectURL(file);
    this.originalPreviewRaw = blobUrl;
    this.originalPreviewUrl = this.sanitizer.bypassSecurityTrustUrl(blobUrl);
    this.originalSize = file.size;
    this.selectedCompressionLevel = this.inputCompressionLevel;
    // use current compression level
    const imgCompressed = await this.compressImage(
      file,
      this.inputCompressionLevel
    );
    this.compressedDataRaw = imgCompressed;
    this.compressedDataUrl =
      this.sanitizer.bypassSecurityTrustUrl(imgCompressed);

    const base64 = imgCompressed.split(",")[1] || "";
    try {
      const binary = atob(base64);
      this.compressedSize = binary.length;
    } catch (e) {
      this.compressedSize = Math.ceil((base64.length * 3) / 4);
    }
  }

  // Called when the compression slider changes. Re-compress the stored original file (if present).
  public async onCompressionChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = parseInt(input.value, 10);
    if (isNaN(value) || value < 1) {
      return;
    }
    this.inputCompressionLevel = Math.max(1, Math.min(100, value));
  }

  public clearAll() {
    // Clear the file input
    const fileInput = document.querySelector(
      'input[name="file-upload"]'
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }

    // Revoke the object URL to free memory
    if (this.originalPreviewRaw) {
      try {
        URL.revokeObjectURL(this.originalPreviewRaw);
      } catch (e) {}
    }

    this.originalFile = null;
    this.originalPreviewRaw = null;
    this.originalPreviewUrl = null;
    this.originalSize = 0;
    this.compressedDataRaw = null;
    this.compressedDataUrl = null;
    this.compressedSize = 0;
  }

  public openOriginalPreview() {
    if (!this.originalPreviewRaw) {
      return;
    }
    const w = window.open(this.originalPreviewRaw, "_blank");
    if (!w) {
      try {
        fetch(this.originalPreviewRaw)
          .then((r) => r.blob())
          .then((b) => {
            const url = URL.createObjectURL(b);
            window.open(url, "_blank");
          })
          .catch(() => {});
      } catch (e) {}
    }
  }

  public openCompressedPreview() {
    if (!this.compressedDataRaw) {
      return;
    }

    const win = window.open("", "_blank");
    if (!win) {
      try {
        window.open(this.compressedDataRaw, "_blank");
      } catch (e) {}
      return;
    }

    try {
      const parts = this.compressedDataRaw.split(",");
      const meta = parts[0] || "";
      const base64 = parts[1] || "";
      const mimeMatch = meta.match(/data:([^;]+);/);
      const mime = mimeMatch && mimeMatch[1] ? mimeMatch[1] : "image/jpeg";

      const binary = atob(base64);
      const len = binary.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binary.charCodeAt(i);
      }

      const blob = new Blob([bytes], { type: mime });
      const url = URL.createObjectURL(blob);

      const html = `<!doctype html><html><head><title>תצוגה מקדמת</title><meta charset="utf-8"><style>html,body{height:100%;margin:0}img{max-width:100%;height:auto;display:block;margin:0 auto}</style></head><body><img src="${url}" alt="compressed image" /></body></html>`;
      win.document.open();
      win.document.write(html);
      win.document.close();

      const cleanup = () => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {}
        try {
          win.removeEventListener("beforeunload", cleanup);
        } catch (e) {}
      };
      win.addEventListener("beforeunload", cleanup);
    } catch (e) {
      try {
        win.location.href = this.compressedDataRaw;
      } catch (err) {}
    }
  }

  public ngOnDestroy() {
    if (this.originalPreviewRaw) {
      try {
        URL.revokeObjectURL(this.originalPreviewRaw);
      } catch (e) {}
      this.originalPreviewRaw = null;
    }
    this.originalPreviewUrl = null;
    this.compressedDataRaw = null;
    this.compressedDataUrl = null;
  }
}



// WEBPACK FOOTER //
// ./src/app/admin/receipt/receipt-util-tests-upload/receipt-util-tests-upload.component.ts